import { appConfig } from "../config";
import { ApiResponse } from "./types";
import { ApiClient } from "./apiClient";
import { OrganizationalUnit } from "./OrganizationalUnitApi";


export const ATTRIBUTE_ENTITIES = {
    EMPLOYEE: 'attributes.entities.employee',
    DOCUMENT: 'attributes.entities.document',
} as const;

export type AttributeEntity = keyof typeof ATTRIBUTE_ENTITIES;

export type FieldType =
    | 'STRING'
    | 'TEXT'
    | 'EMAIL'
    | 'PHONE'
    | 'CURRENCY'
    | 'NUMBER'
    | 'DATE'
    | 'BOOLEAN'
    | 'SINGLE_SELECT'
    | 'MULTI_SELECT'
    | 'ORGANIZATIONAL_UNIT'
    | 'WORK_TIME_MODEL'
    | 'EMPLOYEE_SELECT'
    | 'EMPLOYMENT_TIME_POLICY'
    | 'EMPLOYMENT_LEAVE_POLICY';

// ─── Field configuration types ───────────────────────────────────────────────

export interface BaseFieldConfiguration {
    fieldType: FieldType;
    icon?: string;
    position?: number;
    tableColumn: boolean;
    includeInCreate: boolean;
    filterable: boolean;
    required: boolean;
    calculatedField: boolean;
}

export interface TextFieldConfiguration extends BaseFieldConfiguration {
    fieldType: 'STRING' | 'TEXT';
    minLength?: number;
    maxLength?: number;
}

export interface NumberFieldConfiguration extends BaseFieldConfiguration {
    fieldType: 'NUMBER';
    decimalPlaces: number;
    minValue?: number;
    maxValue?: number;
    step: number;
}

export interface CurrencyFieldConfiguration extends BaseFieldConfiguration {
    fieldType: 'CURRENCY';
    currencyCode: string;
    locale: string;
    allowNegative: boolean;
    minValue?: number;
    maxValue?: number;
}

export interface DateFieldConfiguration extends BaseFieldConfiguration {
    fieldType: 'DATE';
    minDate?: string;  // ISO date string (yyyy-MM-dd)
    maxDate?: string;
}

export interface MultiSelectFieldConfiguration extends BaseFieldConfiguration {
    fieldType: 'MULTI_SELECT';
    minSelections?: number;
    maxSelections?: number;
}

export interface OrgUnitFieldConfiguration extends BaseFieldConfiguration {
    fieldType: 'ORGANIZATIONAL_UNIT';
    showHierarchy: boolean;
}

export type FieldConfiguration =
    | TextFieldConfiguration
    | NumberFieldConfiguration
    | CurrencyFieldConfiguration
    | DateFieldConfiguration
    | MultiSelectFieldConfiguration
    | OrgUnitFieldConfiguration
    | BaseFieldConfiguration;

// ─── AttributeDefinition ─────────────────────────────────────────────────────

export interface AttributeDefinition {
    id?: number;
    name: string;
    label: string;
    fieldType: FieldType;
    selections?: AttributeSelectItem[];
    organizationalUnits?: OrganizationalUnit[];
    value?: any;
    sectionId?: number;
    enabled: boolean;
    nestedAttributeDefinitions?: AttributeDefinition[];
    configuration: FieldConfiguration;
}

export interface AttributeSelectItem {
    id: number;
    itemValue: string;
    displayName: string;
}

export interface WorkTimeConfiguration {
    id: number;
    name: string;
}

export interface AttributeSection {
    id: number;
    name: string;
}

// ─── Request types ────────────────────────────────────────────────────────────

export interface CreateAttributeDefinitionRequest {
    name: string;
    fieldType: FieldType;
    sectionId?: number;
    configuration: Omit<FieldConfiguration, 'fieldType' | 'calculatedField'> & { fieldType?: FieldType };
}

export type UpdateAttributeDefinitionRequest = Partial<CreateAttributeDefinitionRequest>;

// ─── API calls ───────────────────────────────────────────────────────────────

const apiClient = new ApiClient(`${appConfig.backendUrl}/api/attribute-definitions`);

export const fetchAttributeDefinitions = (entity: string = 'Employee'): Promise<ApiResponse<AttributeDefinition[]>> => {
    return apiClient.get<AttributeDefinition[]>(`/${encodeURIComponent(entity)}`);
};

export const fetchAttributeDefinition = (entity: string, name: string): Promise<ApiResponse<AttributeDefinition>> => {
    return apiClient.get<AttributeDefinition>(`/${encodeURIComponent(entity)}/${encodeURIComponent(name)}`);
};

export const createAttributeDefinition = (
    entity: string,
    attributeData: CreateAttributeDefinitionRequest
): Promise<ApiResponse<AttributeDefinition>> => {
    return apiClient.post<AttributeDefinition>(`/${encodeURIComponent(entity)}`, attributeData);
};

export const updateAttributeDefinition = (
    entity: string,
    name: string,
    attributeData: UpdateAttributeDefinitionRequest
): Promise<ApiResponse<AttributeDefinition>> => {
    return apiClient.put<AttributeDefinition>(`/${encodeURIComponent(entity)}/${encodeURIComponent(name)}`, attributeData);
};

export const deleteAttributeDefinition = (entity: string, name: string): Promise<ApiResponse<void>> => {
    return apiClient.delete<void>(`/${encodeURIComponent(entity)}/${encodeURIComponent(name)}`);
};

export const fetchAttributeSections = (): Promise<ApiResponse<AttributeSection[]>> => {
    return apiClient.get<AttributeSection[]>('/sections');
};

// ─── Type guards ─────────────────────────────────────────────────────────────

export const isNumberConfig = (c?: FieldConfiguration): c is NumberFieldConfiguration =>
    c?.fieldType === 'NUMBER';

export const isCurrencyConfig = (c?: FieldConfiguration): c is CurrencyFieldConfiguration =>
    c?.fieldType === 'CURRENCY';

export const isTextConfig = (c?: FieldConfiguration): c is TextFieldConfiguration =>
    c?.fieldType === 'STRING' || c?.fieldType === 'TEXT';

export const isDateConfig = (c?: FieldConfiguration): c is DateFieldConfiguration =>
    c?.fieldType === 'DATE';

export const isMultiSelectConfig = (c?: FieldConfiguration): c is MultiSelectFieldConfiguration =>
    c?.fieldType === 'MULTI_SELECT';

export const isOrgUnitConfig = (c?: FieldConfiguration): c is OrgUnitFieldConfiguration =>
    c?.fieldType === 'ORGANIZATIONAL_UNIT';